import React, { useEffect, useMemo, useState } from 'react';
import AuthLayout from '../../components/layout/AuthLayout';
import { useLazyGetProjectsQuery } from '../../api/projects.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { Link } from 'react-router-dom';
import { PERMISSION_PROJECTS } from '../../utils/constants.js';
import IconAdd from '../../components/images/IconAdd.jsx';
import IconFileText from '../../components/images/IconFileText.jsx';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { debounce } from 'lodash';
import BaseInput from '../../components/form/BaseInput.jsx';
import { getStatusBadgeClass } from '../../utils/project.js';

const Projects = () => {
    const { user, permissions: userPermissions } = useCurrentUser();

    const [isLoading, setIsLoading] = useState(true);
    const [refetch, setRefetch] = useState(false);
    const [projectsResponse, setProjectsResponse] = useState({});
    const organizationSwitch = useSelector((state) => state.organizationSwitch);

    const [append, setAppend] = useState(false);

    const [projects, setProjects] = useState([]);
    const [search, setSearch] = useState('');

    const debouncedSearch = useDebounce(search, 500);
    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);

    const resetSearch = () => {
        setSearch('');
        setPage(1);
    };

    const searchByValue = (value) => {
        setSearch(value);
        setPage(1);
    };

    const requestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id] ? organizationSwitch[user?.id].private : null;
        let organizations =
            organizationSwitch[user?.id] && organizationSwitch[user?.id].organizations
                ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                      (key) => organizationSwitch[user?.id].organizations[key] === true
                  )
                : [];

        if (refetch) {
            setRefetch(false);
        }

        return {
            search: debouncedSearch,
            page,
            ...(organizations.length > 0 ? { organizations: organizations } : {}),
            ...(privateOrg !== null ? { private: privateOrg } : {})
        };
    }, [organizationSwitch, user?.id, debouncedSearch, page, refetch]);

    const [getProjects] = useLazyGetProjectsQuery();

    useEffect(() => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            (async () => {
                const result = await getProjects(requestParams);
                setProjectsResponse(result.data);
            })();
        }
    }, [getProjects, requestParams]);

    useEffect(() => {
        if (projectsResponse?.response) {
            if (append) {
                setProjects((prevState) => {
                    return [...prevState, ...projectsResponse.response.projects];
                });
                setAppend(false);
            } else {
                setProjects(projectsResponse.response.projects);
            }

            setHasMorePages(projectsResponse.response.has_more_pages);
            setIsLoading(false);
            setProjectsResponse({});
        }
    }, [projectsResponse, append]);

    useEffect(() => {
        let processing = false;

        const handleScroll = debounce(() => {
            if (
                Math.round(window.innerHeight + document.documentElement.scrollTop) + 10 >=
                document.documentElement.offsetHeight
            ) {
                if (hasMorePages && !isLoading && !processing) {
                    processing = true;
                    setIsLoading(true);
                    setPage((prevPage) => prevPage + 1);
                    setAppend(true);
                    setTimeout(() => {
                        processing = false;
                    }, 500);
                }
            }
        }, 300);

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [hasMorePages, isLoading]);

    return (
        <AuthLayout
            title='Projects'
            backRoute='/dashboard'
            forwardRoute='/projects/add'
            forwardPermission={PERMISSION_PROJECTS}
            forwardPermissionAttribute='create'
            forwardIcon={IconAdd}
            forwardIconClassName='fill-theme1-white'
        >
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-6'>
                <div className='flex justify-between mt-6 mb-4'>
                    <div className='flex-grow'>
                        <BaseInput
                            name='search'
                            type='text'
                            value={search}
                            placeholder='Search...'
                            setValue={(value) => searchByValue(value)}
                        />
                        <div className='mt-2 text-right'>
                            <Link to='/projects/advanced-search' className='text-base text-theme1-title2 font-bold'>
                                Advanced Search
                            </Link>
                        </div>
                    </div>
                    <div className='text-theme1-primary2 cursor-pointer ml-4 mt-2' onClick={() => resetSearch()}>
                        Reset
                    </div>
                </div>
                {/*{!!userPermissions[PERMISSION_PROJECTS]?.delete && (*/}
                {/*    <div className='flex justify-between mt-4'>*/}
                {/*        <div onClick={() => changeSelectionMode()} className='cursor-pointer'>*/}
                {/*            <IconMultiSelect className='w-6 h-6 fill-theme1-dark' />*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*)}*/}
            </div>
            <div className='bg-theme1-tan text-white py-4 px-8 max-w-3xl w-full mx-auto mb-6'>
                <div className='flex justify-between mb-4'>
                    <div className='flex-1'>
                        <div className={`text-[22px] text-theme1-primary font-bold`}>Projects</div>
                        <div className='text-base text-theme1-title2'>Manage all your projects efficiently</div>
                    </div>
                </div>

                <ul>
                    {projects.map((project) => (
                        <li
                            key={project.id}
                            className='flex items-center justify-between py-4 border-t border-solid border-theme1-tanTransparent'
                        >
                            <div className='flex items-center gap-2'>
                                <IconFileText className='stroke-theme1-dark fill-transparent w-9 h-9' />

                                <div className='flex-grow'>
                                    <Link
                                        to={`/projects/${project.id}`}
                                        className='text-base font-bold text-theme1-title2'
                                    >
                                        {project.name}
                                    </Link>
                                    {project.companies.length > 0 && (
                                        <div className='flex gap-1'>
                                            {project.companies.map((company, index) => (
                                                <div
                                                    key={company.id}
                                                    className='text-xs text-theme1-text2 capitalize mt-1'
                                                >
                                                    {company.name}
                                                    {index < project.companies.length - 1 && (
                                                        <span className='text-theme1-text2 ml-1 relative -top-[1px]'>
                                                            |
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div
                                className={`ml-auto px-3 py-1 rounded-md text-[9px] font-bold text-white capitalize ${getStatusBadgeClass(project.status)}`}
                            >
                                {project.status}
                            </div>
                        </li>
                    ))}
                </ul>
                {isLoading && <ThreeDotsLoader />}
                {/*{selectMode && (*/}
                {/*    <div className='z-20 fixed left-0 bottom-0 w-full bg-auth bg-center bg-cover rounded-t-4xl p-6'>*/}
                {/*        {confirmMassDelete ? (*/}
                {/*            <div>*/}
                {/*                <div className='text-white font-semibold text-lg text-center'>*/}
                {/*                    Are you sure you want to delete {selectedProjects.length} selected*/}
                {/*                    projects?*/}
                {/*                </div>*/}
                {/*                <div className='flex justify-between items-center gap-4 mt-6'>*/}
                {/*                    <BaseButton*/}
                {/*                        className='btn-red-outline !text-theme1-white !border-theme1-white'*/}
                {/*                        textValue={'Cancel'}*/}
                {/*                        onClick={() => setConfirmMassDelete(false)}*/}
                {/*                    />*/}
                {/*                    <BaseButton*/}
                {/*                        className='btn-red-outline !text-theme1-white !border-theme1-white'*/}
                {/*                        textValue={'Delete'}*/}
                {/*                        onClick={() => submitDeleteProjects()}*/}
                {/*                    />*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        ) : (*/}
                {/*            <div className='flex justify-between items-center'>*/}
                {/*                <div className='cursor-pointer' onClick={() => setConfirmMassDelete(true)}>*/}
                {/*                    {selectedProjects.length > 0 && <IconDelete />}*/}
                {/*                </div>*/}
                {/*                <div className='text-white font-semibold text-lg'>*/}
                {/*                    {selectedProjects.length} Selected*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        )}*/}
                {/*    </div>*/}
                {/*)}*/}
            </div>
        </AuthLayout>
    );
};

export default Projects;
